import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { CalendarDays } from 'lucide-react';
import { parseISO } from 'date-fns';
import {
  ALL, PERIOD_PRESETS, presetDays, presetFor, hasPeriod, formatPeriod, formatDate, toDay
} from './reportUtils';

const CUSTOM = 'custom';

// How long a day's calendar stays open after a pick. Long enough that the
// second click of a double-click lands on the calendar (picking the same day
// again) rather than on whatever the page has under it.
const CLOSE_AFTER_PICK_MS = 400;

// One end of a custom range: a button showing the day, opening a one-month
// calendar. Same From/To pattern as the recruitment Dashboard. A click here
// can only ever set this end, so double-clicks and click order don't matter.
const DayField = ({ label, value, onPick, testId }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const date = value !== ALL ? parseISO(value) : undefined;

  const pick = (day) => {
    if (!day) return;
    onPick(toDay(day));
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_AFTER_PICK_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="min-w-[148px] justify-start font-normal" data-testid={testId}>
          <span className="mr-1.5 text-muted-foreground">{label}</span>
          {date ? formatDate(value) : <span className="text-muted-foreground">Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" required selected={date} defaultMonth={date} onSelect={pick} />
      </PopoverContent>
    </Popover>
  );
};

// The Reports date filter: all time, a preset period or a custom From/To.
// Where a report offers it (`basisOptions`), a period also gets a switch for
// what the dates select - candidates added in it, or stage moves made in it.
// `description` says in words what the numbers below now mean.
const ReportPeriodFilter = ({ from, to, onChange, basis, basisOptions, onBasisChange, description }) => {
  // "Custom range…" chosen from the menu: show From/To even while the dates
  // still match a preset, or have not been picked yet.
  const [choosingCustom, setChoosingCustom] = useState(false);

  const period = hasPeriod(from, to);
  const menuValue = choosingCustom ? CUSTOM : (!period ? ALL : (presetFor(from, to) || CUSTOM));

  const handleMenu = (value) => {
    setChoosingCustom(value === CUSTOM);
    if (value === ALL) onChange(ALL, ALL);
    else if (value !== CUSTOM) onChange(...presetDays(value));
  };

  // Each end applies as soon as it is picked; with only one end the period is
  // open on the other side ("from 1 Oct"). A start after the end, or an end
  // before the start, brings the other end along, so the range stays valid.
  const pickEnd = (side) => (day) => {
    const next = { from, to, [side]: day };
    if (next.from !== ALL && next.to !== ALL && next.from > next.to) {
      next[side === 'from' ? 'to' : 'from'] = day;
    }
    onChange(next.from, next.to);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Beside the trigger, not in it: SelectTrigger line-clamps its child spans. */}
        <CalendarDays className="w-4 h-4 text-slate-500" aria-hidden="true" />
        <Select value={menuValue} onValueChange={handleMenu}>
          <SelectTrigger className="w-[160px]" aria-label="Period" data-testid="report-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All time</SelectItem>
            {PERIOD_PRESETS.map((preset) => (
              <SelectItem key={preset.key} value={preset.key}>{preset.label}</SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={CUSTOM}>Custom range…</SelectItem>
          </SelectContent>
        </Select>

        {menuValue === CUSTOM ? (
          <div className="flex flex-wrap items-center gap-2">
            <DayField label="From" value={from} onPick={pickEnd('from')} testId="report-period-from" />
            {/* On phones the two fields stack, and a dash would dangle after From. */}
            <span className="hidden sm:inline text-muted-foreground" aria-hidden="true">–</span>
            <DayField label="To" value={to} onPick={pickEnd('to')} testId="report-period-to" />
          </div>
        ) : period && (
          <span className="text-xs text-muted-foreground" data-testid="report-period-label">{formatPeriod(from, to)}</span>
        )}

        {/* A second click on the chosen option would clear it: keep one chosen. */}
        {period && basisOptions && (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={basis}
            onValueChange={(value) => { if (value) onBasisChange(value); }}
            aria-label="What the dates select"
            className="ml-0 sm:ml-1"
            data-testid="report-basis"
          >
            {basisOptions.map((option) => (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                className="px-3 text-xs data-[state=on]:border-purple-300 data-[state=on]:bg-purple-50 data-[state=on]:text-purple-800"
                data-testid={`report-basis-${option.value}`}
              >
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>
      {period && description && (
        <p className="text-xs text-muted-foreground" data-testid="report-period-description">{description}</p>
      )}
    </div>
  );
};

export default ReportPeriodFilter;
